// 📄 cutover.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2, Circle, RefreshCw,
  Milestone, Calendar, ShieldCheck, Activity, User, Wrench, FlaskConical
} from 'lucide-react';
import { useProject } from '../../../../context/ProjectContext.tsx';
import { saveCutoverConfig, getGeneralsConfig } from '../../../../services/commerce/integrationService.ts';
import { useChecklistIncidentAlerts } from './useChecklistIncidentAlerts.ts';
import { useCasosPruebaFallidos } from '../../../../hooks/useCasosPruebaFallidos.ts';
import { buildChecklistStepKey } from '../../../../data/commerceChecklistCatalog.ts';
import ChecklistIncidentBanner from '../../../Proyectos/components/ChecklistIncidentBanner.tsx';

interface CutoverTask {
  id: string;
  phase: 'pre' | 'live' | 'post';
  title: string;
  desc: string;
  owner: string;
  sapElement?: string;
}

const CUTOVER_TASKS: CutoverTask[] = [
  // Pre-Cutover
  {
    id: 't1',
    phase: 'pre',
    title: 'Credenciales de Producción',
    desc: 'Obtener y validar el acceso de las APIs de producción de SAP Ariba Business Network.',
    owner: 'Ariba Admin',
    sapElement: 'Ariba Network Portal'
  },
  {
    id: 't2',
    phase: 'pre',
    title: 'Certificados SSL (STRUST)',
    desc: 'Cargar la cadena de certificados TLS del data center de producción en el entorno productivo.',
    owner: 'Consultor Basis',
    sapElement: 'STRUST'
  },
  {
    id: 't3',
    phase: 'pre',
    title: 'Destinos RFC / HTTP',
    desc: 'Definir y comprobar la comunicación del destino RFC hacia el endpoint de CIG.',
    owner: 'Consultor Basis',
    sapElement: 'SM59 / SPRO'
  },
  {
    id: 't4',
    phase: 'pre',
    title: 'Code Freeze',
    desc: 'Detener y bloquear toda importación o desarrollo en los ambientes DEV y QAS.',
    owner: 'Functional / PMO',
    sapElement: 'Release Manager'
  },
  // Go-Live
  {
    id: 't5',
    phase: 'live',
    title: 'Transporte de OTs',
    desc: 'Importar y validar la cola de Órdenes de Transporte técnicas al mandante productivo.',
    owner: 'Consultor Basis',
    sapElement: 'STMS'
  },
  {
    id: 't6',
    phase: 'live',
    title: 'Registro de CIG',
    desc: 'Habilitar el proyecto productivo en SAP Integration Suite, Managed Gateway.',
    owner: 'Ariba Admin',
    sapElement: 'Managed Gateway Portal'
  },
  {
    id: 't7',
    phase: 'live',
    title: 'Modelo de Distribución & Partner Profile',
    desc: 'Activar el modelo de distribución lógica y configurar el interlocutor para la salida ORDERS.',
    owner: 'Basis / ABAP',
    sapElement: 'BD64 / WE20'
  },
  {
    id: 't8',
    phase: 'live',
    title: 'Prueba de Conexión de Salida',
    desc: 'Realizar la prueba de conexión y validación de ping HTTP con el CIG en producción.',
    owner: 'Consultor Basis',
    sapElement: 'SM59'
  },
  // Post Go-Live
  {
    id: 't9',
    phase: 'post',
    title: 'Monitoreo de IDocs',
    desc: 'Validar la transmisión de los primeros IDocs reales de órdenes de compra enviados.',
    owner: 'Soporte Basis / Funcional',
    sapElement: 'WE02 / BD87'
  },
  {
    id: 't10',
    phase: 'post',
    title: 'Procesamiento cXML',
    desc: 'Verificar la conversión y la recepción exitosa de la orden de compra en el portal de Ariba.',
    owner: 'Ariba Admin',
    sapElement: 'Ariba Network Inbox'
  },
  {
    id: 't11',
    phase: 'post',
    title: 'Alertas y Logs de Excepciones',
    desc: 'Confirmar el envío de correos de alerta ante IDocs fallidos o errores de conversión.',
    owner: 'Soporte Técnico',
    sapElement: 'OneHub Audit Logs'
  },
  {
    id: 't12',
    phase: 'post',
    title: 'Handover a Soporte (Hypercare)',
    desc: 'Entregar la documentación de la configuración y habilitar el periodo de soporte operacional.',
    owner: 'Project Manager',
    sapElement: 'Operaciones (Run)'
  }
];

export default function CutoverConfig() {
  const { projectId, esCliente } = useProject();
  const [selectedEnv, setSelectedEnv] = useState<'DEV' | 'QAS' | 'PRD'>('DEV');
  const [activeTab, setActiveTab] = useState<'all' | 'pre' | 'live' | 'post'>('all');
  const [completedTasks, setCompletedTasks] = useState<string[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [rawConfig, setRawConfig] = useState<any>(null);

  const incidentAlerts = useChecklistIncidentAlerts(projectId, selectedEnv === 'PRD');
  const { fallidos: casosPruebaFallidos } = useCasosPruebaFallidos(projectId);

  // Carga inicial de Firestore
  useEffect(() => {
    if (!projectId) { setLoadingData(false); return; }
    (async () => {
      try {
        const config = await getGeneralsConfig(projectId);
        if (config) {
          setRawConfig(config);
          loadEnvironmentData(selectedEnv, config);
        }
      } catch (error) {
        console.error("Error cargando configuración de Cutover:", error);
      } finally {
        setLoadingData(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const loadEnvironmentData = (env: 'DEV' | 'QAS' | 'PRD', configSource = rawConfig) => {
    if (!configSource || !configSource.environments?.[env]) {
      setCompletedTasks([]);
      return;
    }
    const envData = configSource.environments[env];
    if (envData.cutover?.completedTasks) {
      setCompletedTasks(envData.cutover.completedTasks);
    } else {
      setCompletedTasks([]);
    }
  };

  useEffect(() => {
    if (rawConfig) loadEnvironmentData(selectedEnv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEnv]);

  const handleEnvChange = (env: 'DEV' | 'QAS' | 'PRD') => {
    setSelectedEnv(env);
    loadEnvironmentData(env);
  };

  // Toggle de Checkbox
  const handleToggleTask = async (taskId: string) => {
    if (esCliente) return; // defensa adicional: el rol Cliente es solo lectura

    let updatedTasks = [...completedTasks];
    if (updatedTasks.includes(taskId)) {
      updatedTasks = updatedTasks.filter(id => id !== taskId);
    } else {
      updatedTasks.push(taskId);
    }
    
    setCompletedTasks(updatedTasks);
    setSaveStatus('saving');

    try {
      if (!projectId) throw new Error("Sin proyecto activo.");
      await saveCutoverConfig(projectId, selectedEnv, updatedTasks);
      
      // Actualizar rawConfig localmente
      const updatedRaw = { ...rawConfig };
      if (!updatedRaw.environments) updatedRaw.environments = {};
      if (!updatedRaw.environments[selectedEnv]) updatedRaw.environments[selectedEnv] = {};
      if (!updatedRaw.environments[selectedEnv].cutover) updatedRaw.environments[selectedEnv].cutover = {};
      updatedRaw.environments[selectedEnv].cutover.completedTasks = updatedTasks;
      setRawConfig(updatedRaw);

      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error("Error guardando cutover task:", error);
      setSaveStatus('error');
    }
  };

  const filteredTasks = activeTab === 'all' 
    ? CUTOVER_TASKS 
    : CUTOVER_TASKS.filter(task => task.phase === activeTab);

  const totalTasksCount = CUTOVER_TASKS.length;
  const completedTasksCount = completedTasks.length;
  const progressPercent = Math.round((completedTasksCount / totalTasksCount) * 100);

  // Clases CSS por Rol Responsable
  const getOwnerBadgeStyle = (owner: string) => {
    if (owner.includes('Basis')) return 'bg-blue-500/10 text-blue-400 border border-blue-500/20';
    if (owner.includes('Ariba')) return 'bg-orange-500/10 text-orange-400 border border-orange-500/20';
    if (owner.includes('ABAP')) return 'bg-purple-500/10 text-purple-400 border border-purple-500/20';
    return 'bg-zinc-800 text-gray-400 border border-zinc-700';
  };

  // Clases CSS por Fase
  const getPhaseHeaderStyle = (phase: 'pre' | 'live' | 'post') => {
    if (phase === 'pre') return 'text-sky-400 bg-sky-500/5 border border-sky-500/10';
    if (phase === 'live') return 'text-emerald-400 bg-emerald-500/5 border border-emerald-500/10';
    return 'text-violet-400 bg-violet-500/5 border border-violet-500/10';
  };

  const getPhaseLabel = (phase: 'pre' | 'live' | 'post') => {
    if (phase === 'pre') return 'Pre-Cutover';
    if (phase === 'live') return 'Go-Live';
    return 'Post Go-Live';
  };

  if (loadingData) {
    return (
      <div className="p-8 text-center text-gray-400 flex flex-col items-center justify-center space-y-3 h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
        <p className="text-sm">Cargando Plan de Cutover en OneHub Cloud...</p>
      </div>
    );
  }

  return (
    <div className="p-2 text-white max-w-6xl mx-auto space-y-6">

      {casosPruebaFallidos.length > 0 && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-xl p-4 flex items-center justify-between gap-3 text-sm animate-fadeIn">
          <div className="flex items-center gap-2.5 min-w-0">
            <FlaskConical className="w-5 h-5 flex-shrink-0" />
            <span className="truncate">
              ⚠️ Hay {casosPruebaFallidos.length} caso{casosPruebaFallidos.length !== 1 ? 's' : ''} de prueba en estado Fallido sin resolver — revisá antes de continuar con el Cutover.
            </span>
          </div>
          <Link to={`/proyecto/${projectId}/pruebas`} className="underline whitespace-nowrap font-medium hover:text-red-300 flex-shrink-0">
            Ver casos de prueba
          </Link>
        </div>
      )}

      {/* SECCIÓN SUPERIOR: Progreso y Controles */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 bg-white/5 border border-white/10 rounded-3xl p-6 backdrop-blur-md">
        
        {/* Progreso Circular/Lineal */}
        <div className="md:col-span-2 flex flex-col justify-center space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Milestone className="w-5 h-5 text-blue-400" />
              <span className="text-sm font-semibold uppercase tracking-wider text-gray-300">Progreso Global del Plan</span>
            </div>
            <span className="text-2xl font-bold font-mono text-blue-400">{progressPercent}%</span>
          </div>
          <div className="w-full bg-zinc-800 h-3 rounded-full overflow-hidden border border-white/5">
            <div 
              className="bg-gradient-to-r from-blue-500 to-indigo-500 h-full transition-all duration-500" 
              style={{ width: `${progressPercent}%` }} 
            />
          </div>
          <div className="flex justify-between text-xs text-gray-400">
            <span>{completedTasksCount} de {totalTasksCount} tareas listas</span>
            {progressPercent === 100 ? (
              <span className="text-emerald-400 font-semibold animate-pulse">🎉 ¡Listo para producción!</span>
            ) : (
              <span>Faltan {totalTasksCount - completedTasksCount} tareas</span>
            )}
          </div>
        </div>

        {/* Ambiente e Indicador de Autoguardado */}
        <div className="flex flex-col items-end justify-center space-y-3 border-t md:border-t-0 md:border-l border-white/10 pt-4 md:pt-0 md:pl-6">
          <div className="w-full">
            <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-widest text-right mb-1.5">
              Entorno Productivo Activo
            </label>
            <div className="flex bg-zinc-800 p-1 rounded-xl border border-white/5 shadow-inner justify-end w-fit ml-auto">
              {(['DEV', 'QAS', 'PRD'] as const).map((env) => (
                <button
                  key={env}
                  type="button"
                  onClick={() => handleEnvChange(env)}
                  className={`px-4 py-1.5 rounded-lg text-xs font-bold tracking-wide transition-all ${
                    selectedEnv === env
                      ? env === 'PRD'
                        ? 'bg-red-500 text-[#fff] shadow-md'
                        : env === 'QAS' ? 'bg-orange-500 text-[#fff] shadow-md' : 'bg-blue-500 text-slate-950 shadow-md'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {env}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
            {saveStatus === 'saving' && (
              <>
                <RefreshCw className="w-3.5 h-3.5 animate-spin text-blue-400" />
                <span className="text-blue-400">Guardando en la nube...</span>
              </>
            )}
            {saveStatus === 'success' && (
              <>
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 animate-pulse" />
                <span className="text-emerald-400">Cambios guardados</span>
              </>
            )}
            {saveStatus === 'idle' && (
              <>
                <ShieldCheck className="w-3.5 h-3.5 text-gray-400" />
                <span>Autoguardado habilitado</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* TABS DE FILTRO DE FASES */}
      <div className="flex bg-white/5 p-1 rounded-2xl border border-white/10 w-fit max-w-full overflow-x-auto gap-1">
        <button
          onClick={() => setActiveTab('all')}
          className={`flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-semibold tracking-wide transition-all ${
            activeTab === 'all' 
              ? 'bg-white/10 text-white shadow' 
              : 'text-gray-400 hover:text-white hover:bg-white/5'
          }`}
        >
          <Activity className="w-3.5 h-3.5" /> Todas las Fases
        </button>
        <button
          onClick={() => setActiveTab('pre')}
          className={`flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-semibold tracking-wide transition-all ${
            activeTab === 'pre' 
              ? 'bg-sky-500/20 text-sky-400 border border-sky-500/30' 
              : 'text-gray-400 hover:text-white hover:bg-white/5 border border-transparent'
          }`}
        >
          <Calendar className="w-3.5 h-3.5" /> Pre-Cutover
        </button>
        <button
          onClick={() => setActiveTab('live')}
          className={`flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-semibold tracking-wide transition-all ${
            activeTab === 'live' 
              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
              : 'text-gray-400 hover:text-white hover:bg-white/5 border border-transparent'
          }`}
        >
          <CheckCircle2 className="w-3.5 h-3.5" /> Go-Live
        </button>
        <button
          onClick={() => setActiveTab('post')}
          className={`flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-semibold tracking-wide transition-all ${
            activeTab === 'post' 
              ? 'bg-violet-500/20 text-violet-400 border border-violet-500/30' 
              : 'text-gray-400 hover:text-white hover:bg-white/5 border border-transparent'
          }`}
        >
          <Wrench className="w-3.5 h-3.5" /> Post Go-Live
        </button>
      </div>

      {/* LISTA DE TAREAS */}
      <div className="space-y-4">
        {filteredTasks.map((task) => {
          const isDone = completedTasks.includes(task.id);
          const taskIncidente = incidentAlerts[buildChecklistStepKey('cutover', task.id)];

          return (
            <div
              key={task.id}
              onClick={esCliente ? undefined : () => handleToggleTask(task.id)}
              className={`flex items-start gap-4 bg-white/5 border border-white/10 p-5 rounded-2xl transition-all duration-300 ${
                esCliente ? 'cursor-default' : 'cursor-pointer hover:bg-white/15'
              } ${
                isDone ? 'border-l-4 border-l-blue-500 bg-blue-500/[0.02]' : esCliente ? '' : 'hover:scale-[1.005]'
              }`}
            >
              {/* Checkbox */}
              <button
                type="button"
                disabled={esCliente}
                className="mt-0.5 text-blue-400 flex-shrink-0"
              >
                {isDone ? (
                  <CheckCircle2 className="w-5 h-5 fill-blue-500 text-zinc-950" />
                ) : (
                  <Circle className="w-5 h-5 text-gray-500 hover:text-blue-400 transition-all" />
                )}
              </button>

              {/* Contenido de la Tarea */}
              <div className="flex-1 space-y-2 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <h4 className={`text-base font-semibold leading-tight ${isDone ? 'line-through text-gray-500' : 'text-white'}`}>
                    {task.title}
                  </h4>
                  
                  {/* Etiquetas de Fase y Propietario */}
                  <div className="flex items-center gap-2 flex-wrap">
                    {activeTab === 'all' && (
                      <span className={`text-[10px] font-mono font-bold uppercase px-2 py-0.5 rounded ${getPhaseHeaderStyle(task.phase)}`}>
                        {getPhaseLabel(task.phase)}
                      </span>
                    )}
                    <span className={`flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${getOwnerBadgeStyle(task.owner)}`}>
                      <User className="w-3 h-3" /> {task.owner}
                    </span>
                  </div>
                </div>

                <p className={`text-sm ${isDone ? 'text-gray-500' : 'text-gray-400'}`}>
                  {task.desc}
                </p>

                {/* Transacción / Elemento SAP */}
                {task.sapElement && (
                  <div className="flex items-center gap-1.5 pt-1">
                    <span className="text-[10px] text-gray-500 font-mono">Elemento / Transacción:</span>
                    <span className="font-mono text-xs bg-zinc-900 text-gray-400 px-2 py-0.5 rounded border border-white/5 select-all">
                      {task.sapElement}
                    </span>
                  </div>
                )}

                {taskIncidente && projectId && (
                  <div onClick={(e) => e.stopPropagation()}>
                    <ChecklistIncidentBanner projectId={projectId} incidente={taskIncidente} compact />
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {filteredTasks.length === 0 && (
          <div className="text-center py-12 bg-white/5 border border-white/10 rounded-2xl text-gray-500">
            Ninguna tarea disponible en esta fase.
          </div>
        )}
      </div>
    </div>
  );
}
